import React, { useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useYearbookState from '../hooks/useYearbookState';
import {
  buildProjectRoutePath,
  getRouteByKey,
  parseYearbookPath,
  YEARBOOK_HOME_ROUTE,
} from '../yearbookRoutes';
import DashboardLayout from './DashboardLayout';
import { EmptyProjectsState, ErrorState } from './ErrorState';
import LoadingState from './LoadingState';
import PanelRenderer from './PanelRenderer';
import ProjectRouteGuard from './ProjectRouteGuard';

/**
 * Authenticated dashboard coordinator.
 * Owns route-derived project/panel state and passes focused props to layout + panels.
 */
export default function YearbookDashboard({ logout }) {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const parsedPath = useMemo(() => parseYearbookPath(location.pathname), [location.pathname]);
  const state = useYearbookState({ routeProjectId: parsedPath.projectId });
  const { setActiveChildId } = state;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState(null);

  const activeRoute = parsedPath.route || YEARBOOK_HOME_ROUTE;
  const activeNav = activeRoute.key;

  const navigate = useCallback((key) => {
    const route = getRouteByKey(key) || YEARBOOK_HOME_ROUTE;
    const projectId = state.activeChild?.project_id || parsedPath.projectId;

    routerNavigate(buildProjectRoutePath(projectId, route.key));
    setMobileOpen(false);
  }, [parsedPath.projectId, routerNavigate, state.activeChild?.project_id]);

  const handleSwitchChild = useCallback((id) => {
    const nextChild = state.children.find((child) => child.id === id);

    if (!nextChild?.project_id) {
      return;
    }

    // Optimistic UI update; the URL remains the durable source of truth after refresh/direct load.
    setActiveChildId(id);
    routerNavigate(buildProjectRoutePath(nextChild.project_id, YEARBOOK_HOME_ROUTE.key));
    setMobileOpen(false);
  }, [routerNavigate, setActiveChildId, state.children]);

  const openUpgrade = useCallback((context = null) => {
    // onClick handlers pass events; only keep deliberate upgrade context objects.
    const normalizedContext = context && typeof context === 'object' && (context.source || context.reason)
      ? context
      : null;

    setUpgradeContext(normalizedContext);
    setShowUpgrade(true);
  }, []);

  const closeUpgrade = useCallback(() => {
    setShowUpgrade(false);
    setUpgradeContext(null);
  }, []);
  const openMobileMenu = useCallback(() => setMobileOpen(true), []);
  const closeMobileMenu = useCallback(() => setMobileOpen(false), []);

  if (state.isLoading) {
    return <LoadingState />;
  }

  if (state.error) {
    return <ErrorState message={state.error} />;
  }

  if (!state.activeChild?.project_id) {
    return <EmptyProjectsState />;
  }

  const activeProjectId = state.activeChild.project_id;

  return (
    <ProjectRouteGuard
      parsedPath={parsedPath}
      activeProjectId={activeProjectId}
      availableChildren={state.children}
    >
      <DashboardLayout
        state={state}
        activeNav={activeNav}
        activeRoute={activeRoute}
        mobileOpen={mobileOpen}
        showUpgrade={showUpgrade}
        upgradeContext={upgradeContext}
        onMenuOpen={openMobileMenu}
        onMobileClose={closeMobileMenu}
        onSwitchChild={handleSwitchChild}
        onNavigate={navigate}
        onUpgrade={openUpgrade}
        onCloseUpgrade={closeUpgrade}
        onLogout={logout}
      >
        <PanelRenderer
          activeNav={activeNav}
          state={state}
          onNavigate={navigate}
          onUpgrade={openUpgrade}
        />
      </DashboardLayout>
    </ProjectRouteGuard>
  );
}
